import { useCallback, useEffect, useRef } from "react"
import type { TouchEvent, WheelEvent } from "react"

import { useTranslation } from "react-i18next"

import type { VoiceTranscriptEntry } from "../../utils/voice-transcript"

import { Central, Check, ChevronRight, Loader2 } from "@/components/icons"
import { NodePill } from "@/components/ui/node-pill"
import { cn } from "@/lib/utils"
import { translateActivityMessage } from "@/utils/activity-message-translator"

import { VoiceCaptionText } from "./voice-caption-text"
import { formatClock, splitActivityText } from "./voice-stage-utils"
import styles from "./voice-stage.module.css"
import { getActivityIcon } from "../activity-message/activity-message.utils"

type VoiceTranscriptWallProps = {
  entries: VoiceTranscriptEntry[]
  /** When the call started, for the time stamps on each turn. */
  sessionStartedAt: number | null
  /** The call is reconnecting right now; only then does the lost-connection line spin. */
  reconnecting?: boolean
  /** `closing` keeps the card mounted for its exit motion. */
  phase: "open" | "closing"
  onClose: () => void
}

function WorkLineIcon({ entry }: { entry: Extract<VoiceTranscriptEntry, { kind: "work" }> }) {
  if (entry.status === "running") return <Loader2 aria-hidden="true" className={styles.spin} />
  if (entry.status === "done" && entry.name) return <Check aria-hidden="true" />
  return <span aria-hidden="true">{getActivityIcon(entry.activity)}</span>
}

function WorkLine({ entry }: { entry: Extract<VoiceTranscriptEntry, { kind: "work" }> }) {
  const { t } = useTranslation()
  const failed = entry.status === "failed" || entry.status === "aborted"
  const { title, note } = splitActivityText(translateActivityMessage(entry.activity, t))
  return (
    <li className={cn(styles.workLine2, styles.guideIn)}>
      <WorkLineIcon entry={entry} />
      <span className={cn(styles.workText, failed && styles.stepFailed)}>
        {title}
        {note && <small className={styles.workNote}>{note}</small>}
      </span>
    </li>
  )
}

/**
 * The transcript, as a cream card on the ground: what you said as a live line
 * while you talk, both sides as turns with a marker and a time, the answer
 * streaming with the chat's cursor, the work steps in between, an
 * "Interrupted" mark where you cut in. Its label doubles as the close control.
 */
export function VoiceTranscriptWall({
  entries,
  sessionStartedAt,
  reconnecting = false,
  phase,
  onClose,
}: VoiceTranscriptWallProps) {
  const { t } = useTranslation()
  const listRef = useRef<HTMLOListElement>(null)

  // Follow the conversation: the newest line stays in view.
  // Someone who has scrolled up to read is left alone until they come back down.
  const followingRef = useRef(true)
  const lastTouchYRef = useRef<number | null>(null)
  const handleScroll = useCallback(() => {
    const list = listRef.current
    if (!list) return
    followingRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < 24
  }, [])

  const unlatchOnUpwardIntent = useCallback((deltaY: number) => {
    if (deltaY < 0) followingRef.current = false
  }, [])

  const handleWheel = useCallback(
    (event: WheelEvent<HTMLOListElement>) => unlatchOnUpwardIntent(event.deltaY),
    [unlatchOnUpwardIntent]
  )

  const handleTouchStart = useCallback((event: TouchEvent<HTMLOListElement>) => {
    lastTouchYRef.current = event.touches.length > 0 ? event.touches[0].clientY : null
  }, [])

  const handleTouchMove = useCallback(
    (event: TouchEvent<HTMLOListElement>) => {
      if (event.touches.length === 0 || lastTouchYRef.current == null) return
      const y = event.touches[0].clientY
      const deltaY = lastTouchYRef.current - y
      lastTouchYRef.current = y
      unlatchOnUpwardIntent(deltaY)
    },
    [unlatchOnUpwardIntent]
  )

  const followNewest = useCallback(() => {
    const list = listRef.current
    if (!list || !followingRef.current) return
    // CSS `.list { scroll-behavior: smooth }` animates `scrollTop = …`.
    const previous = list.style.scrollBehavior
    list.style.scrollBehavior = "auto"
    list.scrollTop = list.scrollHeight
    list.style.scrollBehavior = previous
  }, [])

  useEffect(() => {
    followNewest()
  }, [entries, followNewest])

  const stamp = (at: number) =>
    sessionStartedAt === null ? null : formatClock(at - sessionStartedAt)

  return (
    <aside
      aria-label={t("voice_mode.transcript.title")}
      className={cn(styles.wall, phase === "closing" ? styles.wallClosing : styles.wallOpening)}
      data-testid="voice-transcript"
    >
      <div className={styles.card}>
        <div className={styles.wallHead}>
          <button
            aria-expanded="true"
            className={cn(styles.hang)}
            data-testid="voice-transcript-toggle"
            title={t("voice_mode.transcript.hide")}
            type="button"
            onClick={onClose}
          >
            <NodePill
              className={cn(styles.pill, styles.pillPrimary)}
              connector="left"
              kind="marker"
            >
              <Central.ClosedCaptioning aria-hidden="true" className="node-pill__icon" />
              {t("voice_mode.transcript.title")}
              <span aria-hidden="true" className={styles.live} />
              <ChevronRight aria-hidden="true" className={styles.chev} />
            </NodePill>
          </button>
        </div>
        <ol
          ref={listRef}
          className={styles.list}
          onScroll={handleScroll}
          onTouchMove={handleTouchMove}
          onTouchStart={handleTouchStart}
          onWheel={handleWheel}
        >
          {entries.length === 0 && (
            <li className={styles.empty}>
              {t("voice_mode.transcript.empty")}
              <small>{t("voice_mode.transcript.saved_note")}</small>
            </li>
          )}
          {entries.map((entry) => {
            if (entry.kind === "you") {
              return (
                <li key={entry.id} className={cn(styles.turn, styles.guideIn)}>
                  <span className={styles.mark}>
                    <span aria-hidden="true" className={styles.dot} />
                    {t("voice_mode.you")}
                    {!entry.live && stamp(entry.at) && <time>{stamp(entry.at)}</time>}
                  </span>
                  <p className={cn(styles.text, entry.live && styles.textLive)}>
                    <VoiceCaptionText
                      streaming={Boolean(entry.live)}
                      text={entry.text}
                      onGrow={followNewest}
                    />
                    {entry.live && <span aria-hidden="true" className={styles.cursor} />}
                  </p>
                </li>
              )
            }
            if (entry.kind === "ai") {
              return (
                <li key={entry.id} className={cn(styles.turn, styles.guideIn)}>
                  <span className={styles.mark}>
                    <span aria-hidden="true" className={cn(styles.dot, styles.dotOn)} />
                    {t("voice_mode.ai")}
                    {stamp(entry.at) && <time>{stamp(entry.at)}</time>}
                  </span>
                  <p className={styles.text}>
                    <VoiceCaptionText
                      streaming={Boolean(entry.streaming)}
                      text={entry.text}
                      onGrow={followNewest}
                    />
                    {entry.streaming && <span aria-hidden="true" className={styles.cursor} />}
                    {entry.interrupted && (
                      <span className={styles.cut}>{t("voice_mode.transcript.interrupted")}</span>
                    )}
                  </p>
                </li>
              )
            }
            if (entry.kind === "work") {
              return <WorkLine key={entry.id} entry={entry} />
            }
            return (
              <li key={entry.id} className={cn(styles.system, styles.guideIn)}>
                {entry.code === "connection_lost" && reconnecting && (
                  <Loader2 aria-hidden="true" className={styles.spin} />
                )}
                <span>{t(`voice_mode.transcript.${entry.code}`)}</span>
              </li>
            )
          })}
        </ol>
      </div>
    </aside>
  )
}
